import {
  Content,
  DialogProps,
  Overlay,
  Portal,
  Root,
  Title,
} from '@radix-ui/react-dialog';
import cl from 'clsx';
import { ReactNode, Reducer, useCallback, useReducer } from 'react';

import {
  useCreateAccountWithChannel,
  PollNewChannel,
  AwaitNewChannelForm,
  type PollNewChannelProps,
} from '@/true-fsd/features/AwaitNewChannel';
import {
  CreateAccount,
  CreateAccountComplete,
} from '@/true-fsd/features/CreateAccount';
import {
  CreateAccountWithSite,
  type CreateAccountWithSiteProps,
} from '@/true-fsd/features/CreateSite';
import {
  WorkspaceTypeSelect,
  type WorkspaceTypeSelectProps,
} from '@/true-fsd/features/WorkspaceTypeSelect';
import { Button } from '@/true-fsd/shared/ui/ButtonLink';
import {
  FigmaTransparentLayout,
  type FigmaTransparentLayoutProps,
} from '@/true-fsd/shared/ui/FigmaLayout';

import {
  useNewWsStoreIs,
  AdvertiserAccount,
  Invite,
} from '@/true-fsd/data/advertiser-account';
import { PickNonNullable } from '@synapex/tools/typescript';
import classes from './CreateAccountWizard.module.scss';

type RouteLike = 'create' | 'workspace_type' | 'site' | 'channel' | 'complete';

export type CreateAccountWizardProps = {} & PickNonNullable<
  DialogProps,
  'open' | 'onOpenChange'
>;

type State =
  | {
      route: Exclude<RouteLike, 'complete' | 'workspace_type'>;
    }
  | {
      route: 'workspace_type';
      defaultValue?: 'site' | 'channel';
    }
  | {
      route: 'complete';
      createdAccount: AdvertiserAccount;
      invite: Invite;
    };

type Action = State;

const stateReducer: Reducer<State, Action> = (state, action) => {
  switch (action.route) {
    case 'complete':
    case 'workspace_type': {
      return {
        ...action,
      };
    }

    default: {
      if (state.route === action.route) {
        // not changed
        return state;
      }

      return {
        ...action,
      };
    }
  }
};

const FigmaTransparentLayoutWithHeader = (
  props: Omit<FigmaTransparentLayoutProps, 'RenderHeader'>,
) => <FigmaTransparentLayout {...props} RenderHeader={Title} />;

const CreateAccountWizardContent = ({
  onOpenChange,
}: Omit<CreateAccountWizardProps, 'open'>) => {
  const [state, setState] = useReducer(stateReducer, {
    route: 'create',
  });
  const resetNewWsStore = useNewWsStoreIs((state) => state.reset);

  const hook = useCreateAccountWithChannel();

  const isComplete =
    state.route === 'complete' ||
    (state.route === 'channel' && hook.channelRequest);

  const toWorkspaceType = useCallback(
    () => setState({ route: 'workspace_type' }),
    [],
  );

  const toComplete: NonNullable<PollNewChannelProps['onAccount']> = useCallback(
    (_, { createdAccount, invite }) =>
      setState({ route: 'complete', createdAccount, invite }),
    [],
  );

  const RenderBackToCreate: WorkspaceTypeSelectProps['RenderBack'] =
    useCallback(({ navigate: _, value: __, ...props }) => {
      return (
        <Button {...props} onClick={() => setState({ route: 'create' })} />
      );
    }, []);

  const RenderBackToWorkspaceType: CreateAccountWithSiteProps['RenderBack'] =
    useCallback(({ navigate: _, ...props }) => {
      return (
        <Button
          {...props}
          onClick={() => setState({ route: 'workspace_type' })}
        />
      );
    }, []);

  const RenderForward: WorkspaceTypeSelectProps['RenderForward'] = useCallback(
    ({ navigate: _, value: route, ...props }) => {
      return <Button {...props} onClick={() => setState({ route })} />;
    },
    [],
  );

  const onCloseDone = useCallback(
    (evt: Event) => {
      evt.preventDefault();
      if (isComplete) {
        resetNewWsStore();
        setState({ route: 'create' });
      }
    },
    [resetNewWsStore, isComplete],
  );

  const onCompleteClick = useCallback(
    () => onOpenChange(false),
    [onOpenChange],
  );

  let content: ReactNode;
  switch (state.route) {
    case 'create': {
      content = (
        <CreateAccount
          RenderLayout={FigmaTransparentLayoutWithHeader}
          onNewAccountUpdated={toWorkspaceType}
        />
      );
      break;
    }
    case 'workspace_type': {
      content = (
        <WorkspaceTypeSelect
          RenderLayout={FigmaTransparentLayoutWithHeader}
          RenderBack={RenderBackToCreate}
          RenderForward={RenderForward}
        />
      );
      break;
    }

    case 'site': {
      content = (
        <CreateAccountWithSite
          RenderLayout={FigmaTransparentLayoutWithHeader}
          RenderBack={RenderBackToWorkspaceType}
          onSuccess={toComplete}
        />
      );
      break;
    }

    case 'channel': {
      if (hook.channelRequest) {
        content = (
          <PollNewChannel
            channelRequest={hook.channelRequest}
            RenderLayout={FigmaTransparentLayoutWithHeader}
            onAccount={toComplete}
            onCancel={hook.onCancel}
            onContinue={onCompleteClick}
          />
        );
      } else {
        content = (
          <AwaitNewChannelForm
            mutateAsync={hook.mutateAsyncChannel}
            RenderLayout={FigmaTransparentLayoutWithHeader}
            RenderBack={RenderBackToWorkspaceType}
          />
        );
      }
      break;
    }

    case 'complete': {
      content = (
        <CreateAccountComplete
          RenderLayout={FigmaTransparentLayoutWithHeader}
          account={state.createdAccount}
          invite={state.invite}
          onCompleteClick={onCompleteClick}
        />
      );

      break;
    }
  }

  return (
    <Content
      aria-describedby={undefined}
      onCloseAutoFocus={onCloseDone}
      className={cl(classes.content)}
    >
      {content}
    </Content>
  );
};

export const CreateAccountWizard = ({
  open,
  onOpenChange,
}: CreateAccountWizardProps) => {
  return (
    <Root modal open={open} onOpenChange={onOpenChange}>
      <Portal>
        <Overlay className={classes.overlay}>
          {open && <CreateAccountWizardContent onOpenChange={onOpenChange} />}
        </Overlay>
      </Portal>
    </Root>
  );
};
